/**
 * VisionBilling gRPC adapter for the subscribe journey + subscription status.
 *
 * Presents the auth-store's wire shape (lowercase status/tier strings, Number counts, ISO
 * instants) on top of the `protos.vision.billing.v1.VisionBilling` gRPC-web service, so the
 * store + the billing UI are unaware of the transport.
 */

import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { VisionBilling } from '../gen/vision/billing/v1/billing_service_pb';
import { PlanChangeMode } from '../gen/vision/billing/v1/change_plan_pb';
import {
  subscriptionStatusFromProto,
  checkoutFromProto,
  plansFromProto,
  packsFromProto,
  transactionsFromProto,
  changePlanResultFromProto,
  cancelSubscriptionResultFromProto,
  billingPortalFromProto,
  storageBreakdownFromProto,
  recalculateResultFromProto,
} from './visionBillingMapping';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  return next(req);
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(VisionBilling, transport);
  }
  return cachedClient;
}

export async function fetchSubscriptionStatus() {
  const response = await client().getSubscriptionStatus({ params: {} });
  if (!response.result) {
    throw new Error('GetSubscriptionStatus returned no result');
  }
  return subscriptionStatusFromProto(response.result);
}

export async function fetchStorageBreakdown() {
  const response = await client().getStorageBreakdown({ params: {} });
  if (!response.result) {
    throw new Error('GetStorageBreakdown returned no result');
  }
  return storageBreakdownFromProto(response.result);
}

export async function recalculateStorage() {
  const response = await client().recalculateStorageUsage({ params: {} });
  if (!response.result) {
    throw new Error('RecalculateStorageUsage returned no result');
  }
  return recalculateResultFromProto(response.result);
}

export async function startSubscriptionCheckout(planKey) {
  const response = await client().startSubscriptionCheckout({ params: { planKey } });
  if (!response.result) {
    throw new Error('StartSubscriptionCheckout returned no result');
  }
  return checkoutFromProto(response.result);
}

export async function listSubscriptionPlans() {
  const response = await client().listSubscriptionPlans({ params: {} });
  if (!response.result) {
    throw new Error('ListSubscriptionPlans returned no result');
  }
  return plansFromProto(response.result);
}

export async function listCreditPacks() {
  const response = await client().listCreditPacks({ params: {} });
  if (!response.result) {
    throw new Error('ListCreditPacks returned no result');
  }
  return packsFromProto(response.result);
}

export async function startCreditPackPurchase(packKey) {
  const response = await client().startCreditPackPurchase({ params: { packKey } });
  if (!response.result) {
    throw new Error('StartCreditPackPurchase returned no result');
  }
  return checkoutFromProto(response.result);
}

export async function startProjectCreditPackPurchase(projectId, packKey) {
  const response = await client().startProjectCreditPackPurchase({ params: { projectId, packKey } });
  if (!response.result) {
    throw new Error('StartProjectCreditPackPurchase returned no result');
  }
  return checkoutFromProto(response.result);
}

export async function transferCreditsToProject(projectId, credits) {
  const response = await client().transferCreditsToProject({
    params: { projectId, credits: BigInt(credits) },
  });
  if (!response.result) {
    throw new Error('TransferCreditsToProject returned no result');
  }
  return {
    purchasedBalance: Number(response.result.purchasedBalance),
    projectBalance: Number(response.result.projectBalance),
  };
}

export async function retrieveProjectCredits(projectId) {
  const response = await client().retrieveProjectCredits({ params: { projectId } });
  if (!response.result) {
    throw new Error('RetrieveProjectCredits returned no result');
  }
  return { creditsRefunded: Number(response.result.creditsRefunded) };
}

export async function listCreditTransactions(pageToken) {
  const pagination = pageToken
    ? { pageSize: 20, pageToken: { case: 'nextToken', value: pageToken } }
    : { pageSize: 20 };
  const response = await client().listCreditTransactions({ params: {}, pagination });
  if (!response.result) {
    throw new Error('ListCreditTransactions returned no result');
  }
  return {
    transactions: transactionsFromProto(response.result),
    nextPageToken: response.pagination?.nextPageToken || null,
  };
}

// The billing UI speaks 'immediate' | 'scheduled'; the proto carries a PlanChangeMode enum.
const MODE_TO_PROTO = {
  immediate: PlanChangeMode.IMMEDIATE,
  scheduled: PlanChangeMode.SCHEDULED,
};

export async function changePlan(planKey, mode) {
  const protoMode = MODE_TO_PROTO[mode];
  if (protoMode === undefined) {
    throw new Error(`Unknown plan change mode: ${mode}`);
  }
  const response = await client().changePlan({ params: { planKey, mode: protoMode } });
  if (!response.result) {
    throw new Error('ChangePlan returned no result');
  }
  return changePlanResultFromProto(response.result);
}

export async function cancelScheduledPlanChange() {
  const response = await client().cancelScheduledPlanChange({ params: {} });
  if (!response.result) {
    throw new Error('CancelScheduledPlanChange returned no result');
  }
  return { cancelled: response.result.cancelled };
}

export async function cancelSubscription() {
  const response = await client().cancelSubscription({ params: {} });
  if (!response.result) {
    throw new Error('CancelSubscription returned no result');
  }
  return cancelSubscriptionResultFromProto(response.result);
}

export async function createBillingPortalSession() {
  const response = await client().createBillingPortalSession({ params: {} });
  if (!response.result) {
    throw new Error('CreateBillingPortalSession returned no result');
  }
  return billingPortalFromProto(response.result);
}
